import { useState, useRef, useCallback, useEffect } from "react";

export interface DerivationPart {
  label: string;
  value: number;
  source?: string;
}

export interface DerivedDisplayProps {
  value: number;
  parts: readonly DerivationPart[];
  label?: string;
  size?: "small" | "normal" | "large";
  trigger?: "hover" | "click";
  formatPart?: (part: DerivationPart) => string;
  disabled?: boolean;
}

const SIZE_FONT: Record<NonNullable<DerivedDisplayProps["size"]>, string> = {
  small: "300 24px 'Fraunces', serif",
  normal: "300 36px 'Fraunces', serif",
  large: "300 48px 'Fraunces', serif",
};

const defaultFormatPart = (p: DerivationPart): string =>
  p.value > 0
    ? p.label + " (+" + p.value + ")"
    : p.label + " (" + p.value + ")";

export function DerivedDisplay({
  value,
  parts,
  label,
  size = "normal",
  trigger = "hover",
  formatPart,
  disabled = false,
}: DerivedDisplayProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const fmt = formatPart ?? defaultFormatPart;
  const hasPopover = parts.length > 0;
  const interactive = hasPopover && !disabled;

  useEffect(() => {
    if (trigger !== "click" || !open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [trigger, open]);

  const handleMouseEnter = useCallback(() => {
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setOpen((prev: boolean) => !prev);
  }, []);

  const handleFocus = useCallback(() => {
    if (trigger === "hover") setOpen(true);
  }, [trigger]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (trigger === "hover") {
      if (!rootRef.current || !rootRef.current.contains(e.relatedTarget as Node | null)) {
        setOpen(false);
      }
    }
  }, [trigger]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((prev: boolean) => !prev);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }, [interactive]);

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const ariaLabel = label
    ? label + ": " + value + (hasPopover ? " (derivation available)" : "")
    : "Value " + value + (hasPopover ? " (derivation available)" : "");

  return (
    <div ref={rootRef} style={rootStyle}>
      <div
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={ariaLabel}
        aria-haspopup={interactive ? "dialog" : undefined}
        aria-expanded={interactive ? open : undefined}
        aria-disabled={disabled || undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
        onFocus={interactive ? handleFocus : undefined}
        onBlur={interactive ? handleBlur : undefined}
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: "8px",
          padding: "8px 14px",
          background: "var(--paper-bg)",
          border: "1px solid var(--paper-rule)",
          position: "relative",
          outline: "none",
          cursor: hasPopover
            ? trigger === "hover"
              ? "help"
              : "pointer"
            : "default",
        }}
        onMouseEnter={
          hasPopover && trigger === "hover" ? handleMouseEnter : undefined
        }
        onMouseLeave={
          hasPopover && trigger === "hover" ? handleMouseLeave : undefined
        }
        onClick={
          hasPopover && trigger === "click" ? handleClick : undefined
        }
      >
        {label && (
          <span
            style={{
              font: "500 10px 'JetBrains Mono', monospace",
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--paper-ink-2)",
              fontStyle: "italic",
            }}
          >
            {label}
          </span>
        )}
        <span
          style={{
            font: SIZE_FONT[size],
            fontVariantNumeric: "tabular-nums",
            color: "var(--paper-ink)",
          }}
        >
          {value}
        </span>
        {hasPopover && open && (
          <div
            role="dialog"
            aria-label={(label ? label + " " : "") + "derivation breakdown"}
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 100,
              background: "var(--paper-bg)",
              border: "1px solid var(--paper-rule)",
              padding: "14px 16px",
              minWidth: "220px",
              marginTop: "4px",
              font: "400 12px/1.6 'IBM Plex Sans', sans-serif",
              color: "var(--paper-ink)",
              boxShadow: "0 12px 40px rgba(0,0,0,.18)",
            }}
          >
            <div
              style={{
                font: "500 9px 'JetBrains Mono', monospace",
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--paper-ink-2)",
                marginBottom: "8px",
                paddingBottom: "6px",
                borderBottom: "1px solid var(--paper-rule)",
              }}
            >
              Derivation
            </div>
            <div style={{ fontVariantNumeric: "tabular-nums" }}>
              {parts.map(fmt).join(" + ")}
            </div>
            <div
              style={{
                fontWeight: 600,
                marginTop: "6px",
                paddingTop: "6px",
                borderTop: "1px solid var(--paper-rule)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              = {value}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
